/**
 * Shared authenticated HTTP client for system-api mounts (`/stock`, `/sequence/v1`, …).
 */

import { globalConfigService } from '@syscolect/application'
import { env } from '@/config/env'
import { authService } from '@/services/authService'

export type SystemApiMount =
  | string
  | {
      /** Path after host, e.g. `/stock` or `/sequence/v1`. */
      basePath: string
      /**
       * Fragment in the missing-host Error (defaults to `basePath`).
       * Use when the historical message cites a shorter label than the real mount.
       */
      hostErrorPath?: string
    }

function resolveMount(mount: SystemApiMount): { basePath: string; hostErrorPath: string } {
  if (typeof mount === 'string') {
    return { basePath: mount, hostErrorPath: mount }
  }
  return {
    basePath: mount.basePath,
    hostErrorPath: mount.hostErrorPath ?? mount.basePath,
  }
}

/**
 * Resolve system-api host and append the mount `basePath`.
 * Missing host → Error mentioning `hostErrorPath` (or `basePath`).
 */
export function systemApiBaseUrl(mount: SystemApiMount): string {
  const { basePath, hostErrorPath } = resolveMount(mount)
  const host = (env.systemApiHost ?? globalConfigService.getConfig()?.systemApiHost)?.replace(
    /\/$/,
    '',
  )
  if (!host) {
    throw new Error(
      `VITE_SYSTEM_API_HOST (ou systemApiHost no Global Config) é necessário para ${hostErrorPath}.`,
    )
  }
  return `${host}${basePath}`
}

/**
 * Authenticated fetch under a system-api mount.
 * Order matches the former stock/sequence helpers: token first, then host/base URL.
 *
 * `orgId` is optional because not every mount is org-scoped: `/address/v1`
 * serves UF, municipality and CEP, which belong to no organization (spec 0047
 * D27). The header is sent only when there is an org to send — an empty
 * `X-Organization-Id` would read as "org missing" on a route that does check it.
 */
export async function systemApiFetch<T>(
  mount: SystemApiMount,
  path: string,
  init: RequestInit & { orgId?: string },
): Promise<T> {
  const token = await authService.getIdToken()
  if (!token) throw new Error('Sessão expirada — faça login novamente.')

  const traceId = crypto.randomUUID()
  const { orgId, ...rest } = init
  const res = await fetch(`${systemApiBaseUrl(mount)}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Trace-Id': traceId,
      ...(orgId ? { 'X-Organization-Id': orgId } : {}),
      ...(rest.headers ?? {}),
    },
  })

  const body = (await res.json().catch(() => ({}))) as {
    message?: string
    error?: string
  }

  if (!res.ok) {
    const err = new Error(body.message || body.error || `HTTP ${res.status}`)
    ;(err as Error & { statusCode: number; traceId: string }).statusCode = res.status
    ;(err as Error & { traceId: string }).traceId = traceId
    throw err
  }

  return body as T
}
